import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CityChipRow } from '@/components/riviera/landing-parts';
import {
  EVENT_WINDOW_SLICES,
  isWindowSlice,
  type CityEventCategoryPages,
} from '@/lib/city-event-slices';
import { useLocale } from '@todo/core/i18n/use-locale';
import { CITY_EVENT_CATEGORY_PAGES_SEED_KEY, LIST_BUCKET } from '@todo/core/list-queries';
import { getPrefetched } from '@todo/core/static-prefetch';

/** The category pages the export generated (the baked list); none where nothing was exported. */
function useCategoryPages(): CityEventCategoryPages {
  const [pages] = useState<CityEventCategoryPages>(
    () => getPrefetched<CityEventCategoryPages>(LIST_BUCKET, CITY_EVENT_CATEGORY_PAGES_SEED_KEY) ?? {},
  );
  return pages;
}

/**
 * Links to a city's slice pages — its date windows and exported categories —
 * plus the full /[city]/events page. Shown on the slice pages and on
 * /[city]/events, both of which bake CITY_EVENT_CATEGORY_PAGES_SEED_KEY so no
 * chip names an unexported page.
 */
export function EventSliceChips({
  cityId,
  active,
  inCity,
}: {
  cityId: string;
  /** The current slice, or null on /[city]/events itself. */
  active: string | null;
  inCity: string;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const categoryPages = useCategoryPages();
  const chips = [
    {
      key: 'all',
      label: t('cityEventSlices.allInCity'),
      active: active == null,
      href: { pathname: '/[lang]/[city]/events', params: { lang: locale, city: cityId } } as const,
    },
    ...[...EVENT_WINDOW_SLICES, ...(categoryPages[cityId] ?? [])].map((slice) => ({
      key: slice,
      label: isWindowSlice(slice)
        ? t(`cityEventSlices.windows.${slice}.label`)
        : t(`categories.${slice}`, { defaultValue: t('categories.other') }),
      active: slice === active,
      href: {
        pathname: '/[lang]/[city]/events/[slice]',
        params: { lang: locale, city: cityId, slice },
      } as const,
    })),
  ];
  return <CityChipRow heading={t('cityEventSlices.browseHeading', { inCity })} chips={chips} />;
}
